import { createSelector } from "@reduxjs/toolkit";
import { BreadcrumbData } from "components/StyledUI/StyledBreadcrumb/StyledBreadcrumb";
import { findObjectPaths } from "find-object-paths";
import { get, isString } from "lodash-es";
import { CatalogGroupView, RootReducerState, SiteInfo } from "../reducers";
import { localStorageUtil } from "_foundationExt/utils/storageUtil";
import { selectCurrentLangId, selectOnlineStatus, siteSelector } from "./site";
import { constants } from "@hcl-commerce-store-sdk/utils";
import {
  MARKETPLACE_CATEGORY_IDENFITIFER_PREFIX,
  MARKETPLACE_DISPLAY_NAME_FALLBACK_SERVICEBUND,
  MARKETPLACE_SELLER_ID_SERVICEBUND,
  MARKETPLACE_TOP_CATEGORY_IDENTIFIER,
} from "constants/common";
import { MPSellerSelectionData } from "@efood-commerce-sdk/typescript-axios-transaction-efood";

const EMPTY_ARRAY = [];

export const selectHasOfflineProducts = (state: RootReducerState) =>
  state.catalog.hasOfflineProducts;

export const selectOnlineSellers = (state: RootReducerState) =>
  state.catalog.seller;

export const selectHasOnlineSellers = (state: RootReducerState) =>
  state.catalog.seller?.length > 0;

const sellerDisplayNameComparator = (
  a?: MPSellerSelectionData,
  b?: MPSellerSelectionData
) => {
  if (a === b) {
    return 0;
  }

  // sort null values at the end
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }

  if (a.displayName === b.displayName) {
    return 0;
  }

  // sort null values at the end
  if (a.displayName == null) {
    return 1;
  }
  if (b.displayName == null) {
    return -1;
  }

  return a.displayName.localeCompare(b.displayName);
};
export const selectedSortedOnlineSellers = createSelector(
  [selectOnlineSellers],
  (sellers) => {
    // need to create new array, as original array is read only
    return [...sellers].sort(sellerDisplayNameComparator);
  }
);

export const selectOfflineSellers = (state: RootReducerState) => {
  return state.catalog.offlineAvailableSellers;
};

export const selectAvailableSellers = createSelector(
  [selectedSortedOnlineSellers, selectOnlineStatus, selectOfflineSellers],
  (onlineSellers, onlineStatus, offlineSellers) => {
    return onlineStatus === "offline"
      ? onlineSellers.filter((s) => offlineSellers.includes(s.extSellerId))
      : onlineSellers;
  }
);

export const selectStoreDescriptionAsMPSeller = createSelector(
  [siteSelector, selectCurrentLangId],
  (currentSite, langId) => {
    if (!currentSite || !currentSite.description) {
      return {
        displayName: MARKETPLACE_DISPLAY_NAME_FALLBACK_SERVICEBUND,
        extSellerId: MARKETPLACE_SELLER_ID_SERVICEBUND,
        sellerId: "0",
      };
    }

    // Search for the description in the current language
    const descriptionEntry = currentSite.description.find(
      (desc) => desc.languageId === langId
    );

    if (!descriptionEntry) {
      return {
        displayName: MARKETPLACE_DISPLAY_NAME_FALLBACK_SERVICEBUND,
        extSellerId: MARKETPLACE_SELLER_ID_SERVICEBUND,
        sellerId: "0",
      };
    }

    return {
      displayName:
        (descriptionEntry.description as string) ??
        MARKETPLACE_DISPLAY_NAME_FALLBACK_SERVICEBUND,
      extSellerId: MARKETPLACE_SELLER_ID_SERVICEBUND,
      sellerId: "0",
    };
  }
);

export const selectOnlineSellersAndStore = createSelector(
  [selectedSortedOnlineSellers, selectStoreDescriptionAsMPSeller],
  (sellers, store) => {
    if (!store || !sellers?.length) {
      return EMPTY_ARRAY;
    }

    // need to create new array, as original array is read only
    const sortedSellers = [...sellers].sort(sellerDisplayNameComparator);

    return [store, ...sortedSellers];
  }
);

export const selectAvailableSellersAndStore = createSelector(
  [selectAvailableSellers, selectStoreDescriptionAsMPSeller],
  (sellers, store) => {
    if (!store || !sellers?.length) {
      return EMPTY_ARRAY;
    }

    return [store, ...sellers];
  }
);

/**
 * Selector for retrieving the selected seller selected by the customer.
 *
 *
 * It verifies if the `expiresAt` timestamp is present and has expired.
 * If it is expired, it returns nothing.
 *
 * @returns {string} The selected sellerId if valid, otherwise undefined.
 */
export const selectSelectedMPSellerChoiceFromRedux = (
  state: RootReducerState
) =>
  !state.catalog.sellerChoice ||
  state.catalog.sellerChoice.expiresAt <= Date.now()
    ? undefined
    : state.catalog.sellerChoice;

export const selectSelectedMPSellerIdFromLocalStorage = (
  state: RootReducerState
): string | undefined => {
  return localStorageUtil.get(constants.SELECTED_SELLER) ?? undefined;
};

/**
 * Selector for retrieving the selected sellerId.
 *
 * This function checks the Redux state first. If no seller choice exists in Redux,
 * it falls back to the value stored in localStorage.
 *
 * @returns {string} The selected sellerId if valid, otherwise a the default value @see MARKETPLACE_SELLER_ID_SERVICEBUND.
 */
export const selectSelectedMPSellerId = createSelector(
  [
    selectSelectedMPSellerChoiceFromRedux,
    selectSelectedMPSellerIdFromLocalStorage,
  ],
  (sellerChoiceFromRedux, sellerIdFromLocalStorage) => {
    if (!sellerChoiceFromRedux) {
      return sellerIdFromLocalStorage ?? MARKETPLACE_SELLER_ID_SERVICEBUND;
    }

    return sellerChoiceFromRedux.sellerId;
  }
);

export const categoryTopSelector = (state: RootReducerState) => {
  return state.catalog.categoryTop;
};

export const selectTopCategories = createSelector(
  [categoryTopSelector, selectSelectedMPSellerId],
  (topCategories, selectedSellerId) => {
    if (!topCategories?.length) {
      return topCategories;
    }

    if (
      !selectedSellerId ||
      selectedSellerId === MARKETPLACE_SELLER_ID_SERVICEBUND
    ) {
      return topCategories.filter(
        (c) => c.identifier !== MARKETPLACE_TOP_CATEGORY_IDENTIFIER
      );
    }

    const mpSellerRootCategoryId = `${MARKETPLACE_CATEGORY_IDENFITIFER_PREFIX}${selectedSellerId}`;
    return (
      topCategories
        .find((c) => c.identifier === MARKETPLACE_TOP_CATEGORY_IDENTIFIER)
        ?.catalogGroupView?.find((c) => c.identifier === mpSellerRootCategoryId)
        ?.catalogGroupView ?? []
    );
  }
);

const splitCategoryPath = (
  categoryPath: ReturnType<typeof findObjectPaths>
) => {
  if (isString(categoryPath)) {
    return categoryPath.split(".");
  }
  if (Array.isArray(categoryPath)) {
    return categoryPath[0]?.split(".") ?? [];
  }
  return [];
};

const memoizedSelectTopCategoryByChildCategoryIdCache = new Map<
  string,
  (state: RootReducerState) => CatalogGroupView | undefined
>();
const selectUndefined = () => undefined;
export const makeSelectTopCategoryByChildCategoryId = (
  categoryId?: string | string[]
) => {
  const uniqueID = Array.isArray(categoryId) ? categoryId[0] : categoryId;
  if (!uniqueID) return selectUndefined;

  const cacheEntry =
    memoizedSelectTopCategoryByChildCategoryIdCache.get(uniqueID);
  if (cacheEntry) {
    return cacheEntry;
  }
  const selectTopCategoryByChildCategoryId = createSelector(
    [selectTopCategories],
    (categoryTop) => {
      const categoryPath = findObjectPaths(categoryTop, {
        key: "uniqueID",
        value: uniqueID,
      });

      const splittedCategoryPath = splitCategoryPath(categoryPath);
      const topCategoryPath = splittedCategoryPath.length
        ? splittedCategoryPath[0]
        : null;

      if (!topCategoryPath) return undefined;

      return get<CatalogGroupView[], string, CatalogGroupView | undefined>(
        categoryTop,
        topCategoryPath,
        undefined
      );
    }
  );
  memoizedSelectTopCategoryByChildCategoryIdCache.set(
    uniqueID,
    selectTopCategoryByChildCategoryId
  );

  return selectTopCategoryByChildCategoryId;
};

export const makeSelectBreadcrumbData = (uniqueIDs?: string[]) => {
  const selectBreadcrumbData = createSelector(
    [categoryTopSelector],
    (topCategories) => {
      if (!uniqueIDs?.length) {
        return [];
      }

      const result: BreadcrumbData[] = [];
      topCategories.forEach((category0) => {
        if (uniqueIDs.includes(category0.uniqueID)) {
          result.push({ label: category0.name, value: category0.uniqueID });
          if (category0.catalogGroupView) {
            category0.catalogGroupView.forEach((category1) => {
              if (uniqueIDs.includes(category1.uniqueID)) {
                result.push({
                  label: category1.name,
                  value: category1.uniqueID,
                });
                if (category1.catalogGroupView) {
                  category1.catalogGroupView.forEach((category2) => {
                    if (uniqueIDs.includes(category2.uniqueID)) {
                      result.push({
                        label: category2.name,
                        value: category2.uniqueID,
                      });
                    }
                  });
                }
              }
            });
          }
        }
      });

      return result;
    }
  );

  return selectBreadcrumbData;
};
